import type { DungeonAutomapSnapshot } from './dungeonAutomap';
import type { DungeonBlueprint, DungeonRegion } from './sim/dungeon';

const SVG_NS = 'http://www.w3.org/2000/svg';
const PLAYER_RADIUS = 1.4;
const VIEWBOX_PADDING = 0.25;

interface MapPoint {
  x: number;
  y: number;
}

interface MapBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Match the world's tile projection so north-east/south-west passages lean on screen. */
function project(point: MapPoint): MapPoint {
  return {
    x: point.x - point.y,
    y: point.x + point.y,
  };
}

function projectedRegionCorners(region: DungeonRegion): MapPoint[] {
  return [
    { x: region.x, y: region.y },
    { x: region.x + region.width, y: region.y },
    { x: region.x + region.width, y: region.y + region.height },
    { x: region.x, y: region.y + region.height },
  ].map(project);
}

function projectedRegionPoints(region: DungeonRegion): string {
  return projectedRegionCorners(region)
    .map((point) => `${point.x},${point.y}`)
    .join(' ');
}

function includePoint(bounds: MapBounds | undefined, point: MapPoint): MapBounds {
  if (!bounds) {
    return { minX: point.x, minY: point.y, maxX: point.x, maxY: point.y };
  }
  return {
    minX: Math.min(bounds.minX, point.x),
    minY: Math.min(bounds.minY, point.y),
    maxX: Math.max(bounds.maxX, point.x),
    maxY: Math.max(bounds.maxY, point.y),
  };
}

function includeRegion(bounds: MapBounds | undefined, region: DungeonRegion): MapBounds {
  return projectedRegionCorners(region).reduce<MapBounds | undefined>(includePoint, bounds)!;
}

function includeCircle(
  bounds: MapBounds | undefined,
  center: MapPoint,
  radius: number,
): MapBounds {
  return includePoint(
    includePoint(bounds, { x: center.x - radius, y: center.y - radius }),
    { x: center.x + radius, y: center.y + radius },
  );
}

function markerRadius(kind: DungeonAutomapSnapshot['markers'][number]['kind']): number {
  return kind === 'landmark' ? 1.2 : 1.6;
}

function snapshotViewBox(snapshot: DungeonAutomapSnapshot): string {
  let bounds = snapshot.regions.reduce<MapBounds | undefined>(includeRegion, undefined);
  for (const marker of snapshot.markers) {
    bounds = includeCircle(
      bounds,
      project({ x: marker.position.x + 0.5, y: marker.position.y + 0.5 }),
      markerRadius(marker.kind),
    );
  }
  if (snapshot.player) {
    bounds = includeCircle(
      bounds,
      project({ x: snapshot.player.x + 0.5, y: snapshot.player.y + 0.5 }),
      PLAYER_RADIUS,
    );
  }
  if (!bounds) return '0 0 1 1';
  return `${bounds.minX - VIEWBOX_PADDING} ${bounds.minY - VIEWBOX_PADDING} ${Math.max(1, bounds.maxX - bounds.minX) + VIEWBOX_PADDING * 2} ${Math.max(1, bounds.maxY - bounds.minY) + VIEWBOX_PADDING * 2}`;
}

export interface DungeonMapUi {
  show(blueprint: DungeonBlueprint, snapshot: DungeonAutomapSnapshot): void;
  hide(): void;
}

/** Mount the compact semantic dungeon automap. No dungeon art is inspected here. */
export function initDungeonMapUi(doc: Document = document): DungeonMapUi {
  const root = doc.createElement('aside');
  root.id = 'dungeon-map';
  root.hidden = true;
  root.setAttribute('aria-label', 'Dungeon automap');
  const title = doc.createElement('header');
  title.textContent = 'Dungeon Map';
  const svg = doc.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('role', 'img');
  svg.setAttribute('aria-label', 'Explored dungeon rooms and passages');
  root.append(title, svg);
  doc.body.append(root);

  const node = (name: string, attrs: Record<string, string>): SVGElement => {
    const element = doc.createElementNS(SVG_NS, name);
    for (const [key, value] of Object.entries(attrs)) element.setAttribute(key, value);
    return element;
  };

  return {
    show(_blueprint, snapshot) {
      svg.setAttribute('viewBox', snapshotViewBox(snapshot));
      svg.replaceChildren();
      for (const region of snapshot.regions) {
        svg.append(
          node('polygon', {
            class: 'dungeon-map-region',
            points: projectedRegionPoints(region),
          }),
        );
      }
      for (const marker of snapshot.markers) {
        const center = project({ x: marker.position.x + 0.5, y: marker.position.y + 0.5 });
        svg.append(
          node('circle', {
            class: 'dungeon-map-marker',
            'data-kind': marker.kind,
            cx: String(center.x),
            cy: String(center.y),
            r: String(markerRadius(marker.kind)),
          }),
        );
      }
      if (snapshot.player) {
        const center = project({ x: snapshot.player.x + 0.5, y: snapshot.player.y + 0.5 });
        svg.append(
          node('circle', {
            class: 'dungeon-map-player',
            cx: String(center.x),
            cy: String(center.y),
            r: String(PLAYER_RADIUS),
          }),
        );
      }
      root.hidden = false;
    },
    hide() {
      root.hidden = true;
      svg.replaceChildren();
    },
  };
}
